import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Palette, Code, CheckCircle2, Download, Rocket, Wand2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { themeApi, type MarketplaceTheme, type StoreTheme } from './themeApi';
import { AiGenerationModal } from './AiGenerationModal';

export default function ThemeManager() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'installed' | 'marketplace'>('installed');
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [installingThemeId, setInstallingThemeId] = useState<string | null>(null);

  // Fetch installed themes
  const { data: installedThemes, isLoading: loadingInstalled } = useQuery({
    queryKey: ['store-themes'],
    queryFn: themeApi.listInstalled,
  });

  // Fetch marketplace themes
  const { data: marketplaceThemes, isLoading: loadingMarketplace } = useQuery({
    queryKey: ['marketplace-themes'],
    queryFn: themeApi.listMarketplace,
    enabled: activeTab === 'marketplace'
  });

  // Install theme mutation
  const installMutation = useMutation({
    mutationFn: themeApi.install,
    onMutate: (variables) => {
      setInstallingThemeId(variables as string);
    },
    onSuccess: () => {
      toast.success('Theme installed successfully');
      queryClient.invalidateQueries({ queryKey: ['store-themes'] });
      setActiveTab('installed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to install theme');
    },
    onSettled: () => {
      setInstallingThemeId(null);
    }
  });

  const publishMutation = useMutation({
    mutationFn: themeApi.publish,
    onSuccess: () => {
      toast.success('Theme published');
      queryClient.invalidateQueries({ queryKey: ['store-themes'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to publish theme');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: themeApi.deleteTheme,
    onSuccess: () => {
      toast.success('Theme deleted');
      queryClient.invalidateQueries({ queryKey: ['store-themes'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete theme');
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Themes</h2>
          <p className="text-muted-foreground">Manage your online store's appearance and functionality.</p>
        </div>
        <Button onClick={() => setAiModalOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm">
          <Wand2 className="mr-2 h-4 w-4" />
          Build with AI
        </Button>
      </div>

      <div className="flex space-x-1 border-b">
        <button
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'installed' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('installed')}
        >
          Installed Themes
        </button>
        <button
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'marketplace' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('marketplace')}
        >
          Theme Library
        </button>
      </div>

      {activeTab === 'installed' && (
        <div className="space-y-4">
          {loadingInstalled ? (
            <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : installedThemes?.length === 0 ? (
            <div className="text-center p-8 border rounded-lg bg-muted/50">
              <Palette className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No themes installed</h3>
              <p className="text-muted-foreground mb-4">Install a theme from the library to get started.</p>
              <Button onClick={() => setActiveTab('marketplace')}>Browse Themes</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {installedThemes?.map((st: StoreTheme) => (
                <Card key={st.id} className="flex flex-col">
                  <div className="aspect-video bg-muted relative rounded-t-lg overflow-hidden flex items-center justify-center">
                    <Palette className="h-12 w-12 text-muted-foreground/50" />
                    {st.role === 'MAIN' && (
                      <Badge className="absolute top-3 left-3 bg-green-500 hover:bg-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Active Theme
                      </Badge>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle>{st.theme.name}</CardTitle>
                    <CardDescription>Version {st.installedVersion}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-end gap-3">
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1"
                        onClick={() => navigate(`/store/themes/${st.id}/editor`)}
                      >
                        <Palette className="mr-2 h-4 w-4" /> Customize
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => navigate(`/store/themes/${st.id}/code`)}
                        title="Edit Code"
                      >
                        <Code className="h-4 w-4" />
                      </Button>
                    </div>
                    {st.role !== 'MAIN' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => publishMutation.mutate(st.id)}
                          disabled={publishMutation.isPending}
                        >
                          <Rocket className="mr-2 h-4 w-4" /> Publish
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this theme?')) {
                              deleteMutation.mutate(st.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          title="Delete Theme"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'marketplace' && (
        <div className="space-y-4">
          {loadingMarketplace ? (
            <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {marketplaceThemes?.map((theme: MarketplaceTheme) => (
                <Card key={theme.id} className="flex flex-col">
                  <div className="aspect-video bg-muted relative rounded-t-lg overflow-hidden flex items-center justify-center">
                    <Palette className="h-12 w-12 text-muted-foreground/50" />
                    <Badge variant="secondary" className="absolute top-3 right-3 font-mono">
                      v{theme.currentVersion}
                    </Badge>
                  </div>
                  <CardHeader>
                    <CardTitle>{theme.name}</CardTitle>
                    <CardDescription>{theme.description || 'A beautiful theme for your store.'}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-end">
                    <Button 
                      className="w-full"
                      onClick={() => installMutation.mutate(theme.id)}
                      disabled={installingThemeId === theme.id}
                    >
                      {installingThemeId === theme.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {installingThemeId === theme.id ? 'Installing...' : 'Install Theme'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <AiGenerationModal open={aiModalOpen} onOpenChange={setAiModalOpen} />
    </div>
  );
}
